"use client"

import React, { useState, useEffect } from 'react'
import { X, Plus, Minus, Upload, Star, Clock, DollarSign, Languages, Trash2 } from 'lucide-react'
import { Dialog, Flex, Box, Button, ScrollArea, Tabs } from "@radix-ui/themes"
import { restaurantPortalAPI } from '../services/restaurantApiClient'
import ImageUpload from './ImageUpload'
import toast from 'react-hot-toast'
import { useLanguage } from '../contexts/LanguageContext'

interface Size {
  name: string
  nameAr?: string
  price: number
  originalPrice?: number
  isDefault: boolean
  isAvailable: boolean
  description?: string
  descriptionAr?: string
}

interface FoodItem {
  _id?: string
  name: string
  nameAr?: string
  description: string
  descriptionAr?: string
  image: string
  price: number
  originalPrice?: number
  sizes?: Size[]
  category: string
  categoryAr?: string
  restaurant: string
  ingredients: string[]
  ingredientsAr?: string[]
  allergens: string[]
  allergensAr?: string[]
  nutritionalInfo: {
    calories?: number
    protein?: number
    carbs?: number
    fat?: number
  }
  tags: string[]
  isAvailable: boolean
  isFeatured?: boolean
  isPopular?: boolean
  preparationTime: string
}

type FormDataType = Omit<FoodItem, 'restaurant' | 'isFeatured' | 'isPopular'> & { restaurant: string }

interface RestaurantFoodItemModalProps {
  isOpen: boolean
  onClose: () => void
  foodItem?: FoodItem | null
  mode: 'create' | 'edit'
  restaurantId: string
  onSuccess: () => void
}

const RestaurantFoodItemModal: React.FC<RestaurantFoodItemModalProps> = ({
  isOpen,
  onClose,
  foodItem,
  mode,
  restaurantId,
  onSuccess
}) => {
  const { t } = useLanguage()
  const [activeTab, setActiveTab] = useState('basic')
  const [formData, setFormData] = useState<FormDataType>({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    image: '',
    price: 0,
    originalPrice: undefined,
    sizes: [],
    category: '',
    categoryAr: '',
    restaurant: restaurantId,
    ingredients: [],
    ingredientsAr: [],
    allergens: [],
    allergensAr: [],
    nutritionalInfo: {},
    tags: [],
    isAvailable: true,
    preparationTime: '15-20 min'
  })

  const [newIngredient, setNewIngredient] = useState('')
  const [newIngredientAr, setNewIngredientAr] = useState('')
  const [newAllergen, setNewAllergen] = useState('')
  const [newAllergenAr, setNewAllergenAr] = useState('')
  const [newTag, setNewTag] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const categories = [
    { label: "Appetizer", labelAr: "مقبلات", value: "appetizer" },
    { label: "Main Dish", labelAr: "طبق رئيسي", value: "main-dish" },
    { label: "Side Dish", labelAr: "طبق جانبي", value: "side-dish" },
    { label: "Sandwich", labelAr: "ساندويتش", value: "sandwich" },
    { label: "Dessert", labelAr: "حلويات", value: "dessert" },
    { label: "Beverage", labelAr: "مشروبات", value: "beverage" },
    { label: "Drink", labelAr: "عصائر", value: "drink" }
  ]

  const preparationTimes = [
    '5-10 min', '10-15 min', '15-20 min', '20-30 min', '30-45 min', '45-60 min', '1+ hour'
  ]

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && foodItem) {
        setFormData({
          _id: foodItem._id,
          name: foodItem.name || '',
          nameAr: foodItem.nameAr || '',
          description: foodItem.description || '',
          descriptionAr: foodItem.descriptionAr || '',
          image: foodItem.image || '',
          price: foodItem.price || 0,
          originalPrice: foodItem.originalPrice,
          sizes: foodItem.sizes || [],
          category: foodItem.category || '',
          categoryAr: foodItem.categoryAr || '',
          restaurant: restaurantId,
          ingredients: foodItem.ingredients || [],
          ingredientsAr: foodItem.ingredientsAr || [],
          allergens: foodItem.allergens || [],
          allergensAr: foodItem.allergensAr || [],
          nutritionalInfo: foodItem.nutritionalInfo || {},
          tags: foodItem.tags || [],
          isAvailable: foodItem.isAvailable ?? true,
          preparationTime: foodItem.preparationTime || '15-20 min'
        })
      } else {
        setFormData({
          name: '', nameAr: '', description: '', descriptionAr: '', image: '',
          price: 0, originalPrice: undefined, sizes: [], category: '', categoryAr: '',
          restaurant: restaurantId, ingredients: [], ingredientsAr: [],
          allergens: [], allergensAr: [], nutritionalInfo: {}, tags: [],
          isAvailable: true, preparationTime: '15-20 min'
        })
      }
      setActiveTab('basic')
    }
  }, [isOpen, mode, foodItem, restaurantId])

  const handleInputChange = (field: keyof FormDataType, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleCategoryChange = (value: string) => {
    const cat = categories.find(c => c.value === value)
    setFormData(prev => ({
      ...prev,
      category: value,
      categoryAr: cat?.labelAr || ''
    }))
  }

  // Size management
  const addSize = () => {
    const newSize: Size = {
      name: '', nameAr: '', price: 0, isDefault: formData.sizes?.length === 0,
      isAvailable: true, description: '', descriptionAr: ''
    }
    setFormData(prev => ({ ...prev, sizes: [...(prev.sizes || []), newSize] }))
  }

  const updateSize = (index: number, field: keyof Size, value: any) => {
    setFormData(prev => {
      const sizes = [...(prev.sizes || [])]
      sizes[index] = { ...sizes[index], [field]: value }
      if (field === 'isDefault' && value === true) {
        sizes.forEach((s, i) => { if (i !== index) s.isDefault = false })
      }
      return { ...prev, sizes }
    })
  }

  const removeSize = (index: number) => {
    setFormData(prev => {
      const sizes = (prev.sizes || []).filter((_, i) => i !== index)
      if (sizes.length > 0 && !sizes.some(s => s.isDefault)) {
        sizes[0].isDefault = true
      }
      return { ...prev, sizes }
    })
  }

  const addIngredient = () => {
    if (newIngredient.trim()) {
      setFormData(prev => ({
        ...prev,
        ingredients: [...prev.ingredients, newIngredient.trim()],
        ingredientsAr: [...(prev.ingredientsAr || []), newIngredientAr.trim()]
      }))
      setNewIngredient('')
      setNewIngredientAr('')
    }
  }

  const removeIngredient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
      ingredientsAr: (prev.ingredientsAr || []).filter((_, i) => i !== index)
    }))
  }

  const addAllergen = () => {
    if (newAllergen.trim()) {
      setFormData(prev => ({
        ...prev,
        allergens: [...prev.allergens, newAllergen.trim()],
        allergensAr: [...(prev.allergensAr || []), newAllergenAr.trim()]
      }))
      setNewAllergen('')
      setNewAllergenAr('')
    }
  }

  const removeAllergen = (index: number) => {
    setFormData(prev => ({
      ...prev,
      allergens: prev.allergens.filter((_, i) => i !== index),
      allergensAr: (prev.allergensAr || []).filter((_, i) => i !== index)
    }))
  }

  const addTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }))
      setNewTag('')
    }
  }

  const removeTag = (index: number) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== index) }))
  }

  const validateForm = () => {
    if (!formData.name.trim()) { toast.error('Food item name is required'); return false }
    if (!formData.description.trim()) { toast.error('Description is required'); return false }
    if (!formData.image) { toast.error('Image is required'); return false }
    if (!formData.category) { toast.error('Category is required'); return false }
    if ((!formData.sizes || formData.sizes.length === 0) && (!formData.price || formData.price <= 0)) {
      toast.error('Price is required when no sizes are defined'); return false
    }
    if (formData.sizes && formData.sizes.length > 0) {
      for (const size of formData.sizes) {
        if (!size.name.trim()) { toast.error('All sizes must have a name'); return false }
        if (size.price <= 0) { toast.error('All sizes must have a valid price'); return false }
      }
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      const submitData = {
        ...formData,
        price: formData.sizes && formData.sizes.length > 0
          ? formData.sizes.find(s => s.isDefault)?.price || formData.sizes[0].price
          : parseFloat(formData.price.toString()),
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice.toString()) : undefined
      }
      if (mode === 'create') {
        await restaurantPortalAPI.createFoodItem(submitData)
        toast.success('Food item created successfully')
      } else if (mode === 'edit' && formData._id) {
        await restaurantPortalAPI.updateFoodItem(formData._id, submitData)
        toast.success('Food item updated successfully')
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving food item:', error)
      toast.error(error.response?.data?.message || 'Failed to save food item')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 900 }}>
        <Dialog.Title>{mode === 'create' ? 'Create Food Item' : 'Edit Food Item'}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {mode === 'create' ? 'Add a new food item to your restaurant menu' : 'Update food item information'}
        </Dialog.Description>

        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Trigger value="basic"><Flex align="center" gap="2"><Upload className="w-4 h-4" />Basic Info</Flex></Tabs.Trigger>
            <Tabs.Trigger value="translations"><Flex align="center" gap="2"><Languages className="w-4 h-4" />Translations</Flex></Tabs.Trigger>
            <Tabs.Trigger value="sizes"><Flex align="center" gap="2"><DollarSign className="w-4 h-4" />Sizes & Pricing</Flex></Tabs.Trigger>
            <Tabs.Trigger value="details"><Flex align="center" gap="2"><Star className="w-4 h-4" />Details</Flex></Tabs.Trigger>
            <Tabs.Trigger value="settings"><Flex align="center" gap="2"><Clock className="w-4 h-4" />Settings</Flex></Tabs.Trigger>
          </Tabs.List>

          <Box pt="4">
            <ScrollArea style={{ maxHeight: "60vh" }} scrollbars="vertical">
              <form onSubmit={handleSubmit}>
                <div className="p-6">

                  {/* Basic Info Tab */}
                  {activeTab === 'basic' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Food Item Name (English) *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder="e.g., Margherita Pizza"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Category *
                          </label>
                          <select
                            required
                            value={formData.category}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat.value} value={cat.value}>{cat.label} / {cat.labelAr}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description (English) *
                        </label>
                        <textarea
                          required
                          rows={3}
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Describe the food item..."
                        />
                      </div>

                      <div>
                        <ImageUpload
                          currentImage={formData.image}
                          onImageUploaded={(url) => handleInputChange('image', url)}
                          uploadEndpoint="food-item/:id/image"
                          entityId={formData._id}
                          label="Food Item Image *"
                          maxSize={5}
                          disabled={!formData._id && mode === 'edit'}
                        />
                        {(!formData._id || mode === 'create') && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Or Image URL</label>
                            <input
                              type="url"
                              value={formData.image}
                              onChange={(e) => handleInputChange('image', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                              placeholder="https://example.com/image.jpg"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Translations Tab */}
                  {activeTab === 'translations' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          <Languages className="w-4 h-4 inline mr-2" />
                          Add Arabic translations for your food item to support bilingual menus.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name (English)</label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name (Arabic) - الاسم</label>
                          <input
                            type="text"
                            dir="rtl"
                            value={formData.nameAr || ''}
                            onChange={(e) => handleInputChange('nameAr', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-right"
                            placeholder="أدخل الاسم بالعربية"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (English)</label>
                          <textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (Arabic) - الوصف</label>
                          <textarea
                            rows={3}
                            dir="rtl"
                            value={formData.descriptionAr || ''}
                            onChange={(e) => handleInputChange('descriptionAr', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-right"
                            placeholder="أدخل الوصف بالعربية"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sizes & Pricing Tab */}
                  {activeTab === 'sizes' && (
                    <div className="space-y-6">
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-4">
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                          <DollarSign className="w-4 h-4 inline mr-2" />
                          Add different sizes with their prices. If no sizes are added, the base price will be used.
                        </p>
                      </div>

                      {/* Base Price (when no sizes) */}
                      {(!formData.sizes || formData.sizes.length === 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base Price *</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.price}
                              onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Original Price (for discount)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.originalPrice || ''}
                              onChange={(e) => handleInputChange('originalPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                        </div>
                      )}

                      {/* Sizes List */}
                      <div className="space-y-4">
                        {(formData.sizes || []).map((size, index) => (
                          <div key={index} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-medium text-gray-900 dark:text-white">Size {index + 1}</h4>
                              <button
                                type="button"
                                onClick={() => removeSize(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Size Name (English) *</label>
                                <input
                                  type="text"
                                  value={size.name}
                                  onChange={(e) => updateSize(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                  placeholder="e.g., Small, Medium, Large"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Size Name (Arabic) - الحجم</label>
                                <input
                                  type="text"
                                  dir="rtl"
                                  value={size.nameAr || ''}
                                  onChange={(e) => updateSize(index, 'nameAr', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-right"
                                  placeholder="صغير، وسط، كبير"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Price *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={size.price}
                                  onChange={(e) => updateSize(index, 'price', parseFloat(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Original Price</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={size.originalPrice || ''}
                                  onChange={(e) => updateSize(index, 'originalPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              <div className="flex items-center">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={size.isDefault}
                                    onChange={(e) => updateSize(index, 'isDefault', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 mr-2"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">Default</span>
                                </label>
                              </div>
                              <div className="flex items-center">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={size.isAvailable}
                                    onChange={(e) => updateSize(index, 'isAvailable', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 mr-2"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">Available</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={addSize}
                        className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Size Option
                      </button>

                      {/* Preparation Time */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preparation Time</label>
                        <select
                          value={formData.preparationTime}
                          onChange={(e) => handleInputChange('preparationTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                        >
                          {preparationTimes.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}


                  {/* Details Tab */}
                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      {/* Ingredients with translations */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ingredients</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <input
                            type="text"
                            value={newIngredient}
                            onChange={(e) => setNewIngredient(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                            placeholder="Ingredient (English)"
                          />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              dir="rtl"
                              value={newIngredientAr}
                              onChange={(e) => setNewIngredientAr(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-right"
                              placeholder="المكون (عربي)"
                            />
                            <button
                              type="button"
                              onClick={addIngredient}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.ingredients.map((ingredient, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              {ingredient}
                              {formData.ingredientsAr?.[index] && <span className="mx-1 text-gray-400">|</span>}
                              {formData.ingredientsAr?.[index]}
                              <button type="button" onClick={() => removeIngredient(index)} className="ml-1 text-gray-500 hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Allergens with translations */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allergens</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <input
                            type="text"
                            value={newAllergen}
                            onChange={(e) => setNewAllergen(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                            placeholder="Allergen (English)"
                          />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              dir="rtl"
                              value={newAllergenAr}
                              onChange={(e) => setNewAllergenAr(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-right"
                              placeholder="مسبب الحساسية (عربي)"
                            />
                            <button
                              type="button"
                              onClick={addAllergen}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.allergens.map((allergen, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                              {allergen}
                              {formData.allergensAr?.[index] && <span className="mx-1 text-red-400">|</span>}
                              {formData.allergensAr?.[index]}
                              <button type="button" onClick={() => removeAllergen(index)} className="ml-1 text-red-500 hover:text-red-700">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Tags */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags</label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                            placeholder="Add tag (e.g., spicy, vegetarian)"
                          />
                          <button type="button" onClick={addTag} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              {tag}
                              <button type="button" onClick={() => removeTag(index)} className="ml-1 text-blue-500 hover:text-blue-700">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Nutritional Information */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nutritional Information (Optional)</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Calories</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.nutritionalInfo.calories || ''}
                              onChange={(e) => handleInputChange('nutritionalInfo', { ...formData.nutritionalInfo, calories: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Protein (g)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.nutritionalInfo.protein || ''}
                              onChange={(e) => handleInputChange('nutritionalInfo', { ...formData.nutritionalInfo, protein: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Carbs (g)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.nutritionalInfo.carbs || ''}
                              onChange={(e) => handleInputChange('nutritionalInfo', { ...formData.nutritionalInfo, carbs: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Fat (g)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.nutritionalInfo.fat || ''}
                              onChange={(e) => handleInputChange('nutritionalInfo', { ...formData.nutritionalInfo, fat: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Settings Tab */}
                  {activeTab === 'settings' && (
                    <div className="space-y-6">
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.isAvailable}
                            onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Available for Order</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Customers can order this item</p>
                      </div>
                    </div>
                  )}
                </div>

                <Flex gap="3" mt="6" pt="6" justify="end" style={{ borderTop: "1px solid var(--gray-5)" }}>
                  <Dialog.Close>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </Dialog.Close>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Food Item' : 'Update Food Item'}
                  </Button>
                </Flex>
              </form>
            </ScrollArea>
          </Box>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default RestaurantFoodItemModal
